import { useState, useEffect, useRef } from 'react';
import { CheckIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useSchool } from '@/contexts/SchoolContext';
import { useRoleSwitcher } from '@/features/auth/hooks';
import schoolNotificationService from '@/features/notifications/services/schoolNotificationService';
import NotifyTargetsEditor from '@/components/shared/NotifyTargetsEditor';

/**
 * DisciplineBriefSettings — school settings for the weekly discipline brief.
 *
 * The brief is the staff-facing "you're covered" reassurance: a midweek roundup
 * of mentions, consequences and anything awaiting review, delivered to whoever
 * runs discipline so they can act (e.g. set a Friday detention) before week's
 * end. Backend job: functions/scheduled/weeklyDisciplineBrief.js.
 *
 * Reads/writes the weeklyBrief* keys on schools/{id}.notificationSettings.
 * Recipients reuse the notifyTargets pattern (named staff + org positions), the
 * same nomination model as consequence-type notifies — so if left empty the job
 * falls back to the "Head of Discipline" org position.
 *
 * Designed to embed inside the discipline settings panel (additive).
 */
const DEFAULT_BRIEF_SETTINGS = {
  weeklyBriefEnabled: false,
  weeklyBriefDay: 'thursday',
  weeklyBriefTime: '15:00',
  weeklyBriefRecipients: [],
};

export default function DisciplineBriefSettings({ embedded = false }) {
  const { schoolId } = useSchool();
  const { userPermissions } = useRoleSwitcher();
  const canEdit = userPermissions?.admin;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_BRIEF_SETTINGS);

  const loadedRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      if (!schoolId || loadedRef.current) return;
      setLoading(true);
      try {
        const s = await schoolNotificationService.getSchoolNotificationSettings(schoolId);
        setSettings({
          weeklyBriefEnabled: s?.weeklyBriefEnabled ?? false,
          weeklyBriefDay: s?.weeklyBriefDay ?? 'thursday',
          weeklyBriefTime: s?.weeklyBriefTime ?? '15:00',
          weeklyBriefRecipients: Array.isArray(s?.weeklyBriefRecipients) ? s.weeklyBriefRecipients : [],
        });
        loadedRef.current = true;
      } catch (err) {
        console.error('[DisciplineBriefSettings] load failed:', err);
        setError('Could not load brief settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [schoolId]);

  // Persist a concrete settings object (passed explicitly so callers never race
  // a stale `settings` closure). Writes only the brief's own keys — scoped so a
  // sibling notification setting can never block the brief from saving.
  const persist = async (nextSettings) => {
    if (!schoolId) return;
    setSaving(true);
    setError(null);
    try {
      await schoolNotificationService.updateWeeklyBriefSettings(schoolId, nextSettings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('[DisciplineBriefSettings] save failed:', err);
      setError('Could not save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Every change persists on the spot — no Save button. Each edit (toggle, day,
  // time, recipients) is a complete intent; it should just stick.
  const update = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    persist(next);
  };

  if (loading) {
    return <div className="py-6 text-center"><span className="loading loading-spinner loading-sm" /></div>;
  }

  if (!canEdit) {
    return <p className="text-sm text-base-content/50 py-6 text-center">You don't have permission to change these settings.</p>;
  }

  const Wrapper = embedded ? 'div' : 'div';

  return (
    <Wrapper className={embedded ? 'mt-8 pt-6 border-t border-base-200' : 'p-6 max-w-2xl mx-auto'}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <ClipboardDocumentListIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-sm font-semibold">Weekly discipline brief</h3>
          <p className="text-xs text-base-content/60 mt-0.5">
            A midweek roundup for whoever runs discipline — mentions, consequences and anything
            awaiting review, in time to act before Friday.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error alert-sm mb-4 text-sm">{error}</div>
      )}

      {/* Enable toggle */}
      <label className="label cursor-pointer justify-start gap-4 mb-2">
        <input
          type="checkbox"
          className="toggle toggle-primary"
          checked={settings.weeklyBriefEnabled}
          onChange={(e) => update('weeklyBriefEnabled', e.target.checked)}
        />
        <span className="label-text font-medium">Send the weekly brief</span>
      </label>

      {settings.weeklyBriefEnabled && (
        <div className="space-y-5 pl-4 border-l-2 border-primary/20 mt-3">
          {/* Day + time */}
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="label-text text-xs">Day</label>
              <select
                className="select select-bordered select-sm w-44"
                value={settings.weeklyBriefDay}
                onChange={(e) => update('weeklyBriefDay', e.target.value)}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="label-text text-xs">Time</label>
              <input
                type="time"
                className="input input-bordered input-sm w-44"
                value={settings.weeklyBriefTime}
                onChange={(e) => update('weeklyBriefTime', e.target.value)}
              />
              <span className="text-[11px] text-base-content/50">South Africa time (SAST)</span>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <p className="text-xs font-medium mb-2">Who receives it</p>
            <NotifyTargetsEditor
              schoolId={schoolId}
              value={settings.weeklyBriefRecipients}
              onChange={(next) => update('weeklyBriefRecipients', next)}
              addStaffLabel="Add staff member"
              emptyHint="No one set — the brief goes to the Head of Discipline by default."
            />
          </div>
        </div>
      )}

      {/* Auto-save status — changes persist on the spot, no button. */}
      <div className="flex justify-end items-center gap-1.5 mt-4 h-4 text-xs text-base-content/50">
        {saving ? (
          <><span className="loading loading-spinner loading-xs" /> Saving…</>
        ) : saved ? (
          <><CheckIcon className="w-3.5 h-3.5 text-success" /> Saved</>
        ) : null}
      </div>
    </Wrapper>
  );
}
